import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import moment from 'moment';
import { LinearProgress } from "@mui/material";
import FacilitiesLocationController from './FacilitiesLocationController.js';
import GameSelector from '../Retail/components/GameSelector.js';
import FacilitiesHeader from './components/FacilitiesHeader.js';
import FacilitiesOverview from './FacilitiesOverview.js';
import RestroomCard from './components/RestroomCard.js';
import RestroomModal from './components/RestroomModal.js';
import VisitsChart from './charts/VisitsChart.js';
import OverviewInsights from '../Concession/components/OverviewInsights';
import { buildFacilitiesInsights } from './facilitiesInsights';
import { USE_DUMMY_FACILITIES_DATA } from './dummyLiveData';
import { ChartSegmented } from '../Concession/charts/chartTheme';
import { InfoTip, Panel, PanelHeader } from '../Concession/components/ConcessionUI';
import AnalysisWindow from '../Concession/components/AnalysisWindow';
import {
  CLEANING_THRESHOLD, DUE_SOON_AT, buildRestroomStats, combinedVisitSeries, recentRate, visitFlow, peakQuarter,
  nowMinute, minuteToClock, clockToMinute, formatRate, formatDueIn, queueColor,
} from './facilitiesMetrics';
import '../Concession/concession.css';
import './facilities.css';

// `text` for values that are words (a restroom name) rather than numbers: smaller, and allowed to wrap
const Kpi = ({ label, value, accent, caption, info, text }) => (
  <div className="cx-kpi">
    <div className="cx-kpi-label">
      {label}
      {info && <InfoTip title={label} text={info} />}
    </div>
    <div className={`cx-kpi-value${text ? ' fx-kpi-text' : ''}`} style={accent ? { color: accent } : undefined}>{value}</div>
    {caption && <div className="cx-kpi-foot">{caption}</div>}
  </div>
);

// Headline numbers across every restroom
const FacilitiesKpis = ({ stats, combined, isLive, windowLabel, range }) => {
  const visits = stats.reduce((sum, s) => sum + s.visits, 0);
  const due = stats.reduce((sum, s) => sum + s.dueCount, 0);
  const busiest = stats.reduce((top, s) => (!top || s.visits > top.visits ? s : top), null);
  const busiestShare = busiest && visits ? Math.round((busiest.visits / visits) * 100) : 0;
  const across = windowLabel || `Across ${stats.length} restroom${stats.length === 1 ? '' : 's'}`;
  const anyQueue = stats.some((s) => s.hasQueue || s.restroom.hasQueueSensors);
  const busiestKpi = (
    <Kpi
      label="Busiest restroom"
      text={!!(busiest && busiest.visits)}
      value={busiest && busiest.visits ? busiest.label : '—'}
      caption={busiest && busiest.visits ? `${busiest.visits.toLocaleString()} entries · ${busiestShare}% of all` : 'No entries recorded'}
    />
  );
  const dueKpi = (
    <Kpi
      label="Cleanings due"
      value={due.toLocaleString()}
      caption={`One every ${CLEANING_THRESHOLD} entries per restroom`}
      info={`There are no cleaning records yet, so a cleaning is counted as due each time a restroom reaches another ${CLEANING_THRESHOLD} entries.`}
    />
  );

  if (isLive) {
    const soon = stats.filter((s) => s.cycle.dueSoon).sort((a, b) => a.cycle.toGo - b.cycle.toGo);
    const withQueue = stats.filter((s) => s.queueNow);
    const longest = withQueue.reduce((top, s) => (!top || s.queueNow.value > top.queueNow.value ? s : top), null);
    return (
      <div className="cx-kpi-grid">
        <Kpi
          label="Entries today"
          value={visits.toLocaleString()}
          caption={`${formatRate(recentRate(combined))} / min now`}
          info="Entries to every restroom today. The rate is the average over the last 10 minutes."
        />
        {anyQueue && (
          <Kpi
            label="Longest queue"
            value={longest ? `${longest.queueNow.value} min` : '—'}
            accent={longest ? queueColor(longest.queueNow.value) : undefined}
            caption={longest ? longest.label : 'No recent queue readings'}
            info="The longest queue at any restroom right now, from each restroom's latest reading."
          />
        )}
        <Kpi
          label="Due soon"
          value={soon.length}
          accent={soon.length ? 'var(--q-warn)' : undefined}
          caption={soon.length
            ? `Next: ${soon[0].label}${soon[0].dueIn != null ? `, ${formatDueIn(soon[0].dueIn)}` : ''}`
            : `None within ${CLEANING_THRESHOLD - DUE_SOON_AT} entries`}
          info={`Restrooms within ${CLEANING_THRESHOLD - DUE_SOON_AT} entries of their next cleaning (${DUE_SOON_AT} or more of ${CLEANING_THRESHOLD}).`}
        />
        {dueKpi}
        {!anyQueue && busiestKpi}
      </div>
    );
  }

  const flow = visitFlow(combined, 1).filter((b) => !range || (b.start >= range.start && b.start < range.end));
  const rush = peakQuarter(flow);
  const peakAt = stats.reduce((top, s) => (s.queuePeak && (!top || s.queuePeak.value > top.queuePeak.value) ? s : top), null);
  return (
    <div className="cx-kpi-grid">
      <Kpi label="Total entries" value={visits.toLocaleString()} caption={across} />
      {anyQueue && (
        <Kpi
          label="Peak queue"
          value={peakAt ? `${peakAt.queuePeak.value} min` : '—'}
          accent={peakAt ? queueColor(peakAt.queuePeak.value) : undefined}
          caption={peakAt ? `${peakAt.label} at ${minuteToClock(peakAt.queuePeak.minute)}` : 'No queue readings'}
          info="The longest queue reading at any restroom in the analysis window."
        />
      )}
      {dueKpi}
      {!anyQueue && <Kpi
        label="Busiest 15 min"
        value={rush ? rush.total.toLocaleString() : '—'}
        caption={rush ? `${minuteToClock(rush.start)} – ${minuteToClock(rush.end)}` : 'No entries recorded'}
        info="The quarter-hour with the most entries across every restroom."
      />}
      {busiestKpi}
    </div>
  );
};

// Card order. Ties keep section order.
const sortRestrooms = (stats, sortBy, isLive) => {
  if (sortBy === 'section') return stats;
  const value = (s) => {
    if (sortBy === 'queue') {
      const queue = isLive ? s.queueNow : s.queuePeak;
      return queue ? queue.value : -1;
    }
    if (sortBy === 'visits') return s.visits;
    if (sortBy === 'due') return s.dueCount * 100000 + s.visits;
    // Next cleaning: fewest entries to go first
    return -s.cycle.toGo;
  };
  return stats
    .map((s, i) => ({ s, i }))
    .sort((a, b) => value(b.s) - value(a.s) || a.i - b.i)
    .map(({ s }) => s);
};

const FacilitiesDashboard = ({ restrooms, currentGame, selectedDate, isToday, now, lastUpdated }) => {
  const [openStats, setOpenStats] = useState(null);
  // null until picked: queue first when restrooms have queue readings, otherwise next cleaning (live) or visits
  const [pickedSort, setSortBy] = useState(null);
  const closeModal = useCallback(() => setOpenStats(null), []);
  // "Next cleaning" is live only, "Cleanings due" past games only
  useEffect(() => {
    setSortBy((s) => (isToday ? (s === 'due' ? 'next' : s) : s === 'next' ? 'visits' : s));
  }, [isToday]);

  const dateKey = moment(selectedDate).format('YYYY-MM-DD');
  const liveNow = isToday ? nowMinute(now) : null;

  const dayStats = useMemo(
    () => restrooms.map((r) => buildRestroomStats(r, dateKey, liveNow)),
    [restrooms, dateKey, liveNow]
  );
  const combined = useMemo(() => combinedVisitSeries(dayStats.map((s) => s.series)), [dayStats]);

  // Analysis window (past games, like concessions and gates): minutes of the game day
  const dayStart = useMemo(() => moment(dateKey, 'YYYY-MM-DD').startOf('day').valueOf(), [dateKey]);
  const toTime = useCallback((minute) => new Date(dayStart + minute * 60000), [dayStart]);
  const dayRange = combined.length ? { start: combined[0].minute, end: combined[combined.length - 1].minute } : null;
  const [picked, setPicked] = useState(null);
  const handleRangeChange = useCallback((from, to) => {
    setPicked({ start: Math.round((from.getTime() - dayStart) / 60000), end: Math.round((to.getTime() - dayStart) / 60000) });
  }, [dayStart]);
  const activeWindow = !isToday && picked && dayRange && (picked.start > dayRange.start || picked.end < dayRange.end)
    ? picked
    : null;
  const activity = useMemo(
    () => visitFlow(combined, 1).map((b) => ({ t: toTime(b.start).getTime(), y: b.visits })),
    [combined, toTime]
  );
  const gatesOpenAt = clockToMinute(currentGame?.gatesOpen);
  const gameStartAt = clockToMinute(currentGame?.gameStartTime);

  // With a window picked, every number follows it; otherwise the day's totals
  const stats = useMemo(
    () => (activeWindow ? restrooms.map((r) => buildRestroomStats(r, dateKey, liveNow, activeWindow)) : dayStats),
    [activeWindow, restrooms, dateKey, liveNow, dayStats]
  );
  const totalVisits = stats.reduce((sum, s) => sum + s.visits, 0);
  const insights = useMemo(
    () => buildFacilitiesInsights({ stats, range: activeWindow, currentGame, isLive: isToday }),
    [stats, activeWindow, currentGame, isToday]
  );
  const windowLabel = activeWindow ? `${minuteToClock(activeWindow.start)} – ${minuteToClock(activeWindow.end)}` : null;

  // Keep the open modal in step with refreshed data
  const modalStats = openStats ? stats.find((s) => s.id === openStats.id) || openStats : null;

  if (!restrooms.length) {
    return (
      <div className="cx-empty-state">
        <div className="cx-empty-title">No restroom data for this game</div>
        <div>Pick another game above, or check back once the gates open.</div>
      </div>
    );
  }

  const anyQueue = stats.some((s) => s.hasQueue || s.restroom.hasQueueSensors);
  const sortBy = pickedSort || (anyQueue ? 'queue' : isToday ? 'next' : 'visits');
  const queueOption = anyQueue ? [{ value: 'queue', label: isToday ? 'Queue now' : 'Peak queue' }] : [];
  const sortOptions = isToday
    ? [
      ...queueOption,
      { value: 'next', label: 'Next cleaning' },
      { value: 'visits', label: 'Entries' },
      { value: 'section', label: 'Section' },
    ]
    : [
      ...queueOption,
      { value: 'visits', label: 'Entries' },
      { value: 'due', label: 'Cleanings due' },
      { value: 'section', label: 'Section' },
    ];

  return (
    <>
      {!isToday && dayRange && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="Analysis window"
              subtitle="Pick a preset or drag the handles. Every number and chart below follows this range."
            />
            <AnalysisWindow
              earliestTime={toTime(dayRange.start)}
              latestTime={toTime(dayRange.end)}
              onRangeChange={handleRangeChange}
              activity={activity}
              gameStart={gameStartAt != null ? toTime(gameStartAt).getTime() : undefined}
              gatesOpen={gatesOpenAt != null ? toTime(gatesOpenAt).getTime() : undefined}
            />
          </Panel>
        </div>
      )}

      <FacilitiesKpis stats={stats} combined={combined} isLive={isToday} windowLabel={windowLabel} range={activeWindow} />

      {insights.length > 0 && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="What stands out"
              subtitle={isToday
                ? 'Findings from today so far, most important first. They update with the live data every minute.'
                : `Findings ${windowLabel ? `for ${windowLabel}` : 'from this game'}, most important first.`}
              actions={isToday && lastUpdated ? (
                <span className="cx-updated fx-updated">
                  <span className="pulsing-dot" />
                  Updated {moment(lastUpdated).format('h:mm A')}
                </span>
              ) : undefined}
            />
            <OverviewInsights insights={insights} />
          </Panel>
        </div>
      )}

      <div className="fx-section-head">
        <div className="fx-section-title">
          <h2 className="cx-panel-title">Restrooms</h2>
          <span className="fx-section-note">
            {isToday
              ? `Live queue and entries toward each restroom's next cleaning (every ${CLEANING_THRESHOLD} entries).`
              : `Peak queue, entries and cleanings due for each restroom${windowLabel ? `, ${windowLabel}` : ''}.`} Select a restroom for its charts.
          </span>
        </div>
        <ChartSegmented label="Sort by" options={sortOptions} value={sortBy} onChange={setSortBy} />
      </div>
      <div className="fx-rcard-grid">
        {sortRestrooms(stats, sortBy, isToday).map((s) => (
          <RestroomCard
            key={s.id}
            stats={s}
            isLive={isToday}
            share={totalVisits ? (s.visits / totalVisits) * 100 : 0}
            toTime={toTime}
            range={activeWindow}
            gatesOpen={gatesOpenAt}
            gameStart={gameStartAt}
            onOpen={setOpenStats}
          />
        ))}
      </div>

      <VisitsChart series={combined} currentGame={currentGame} range={activeWindow} who="all restrooms" />

      <RestroomModal stats={modalStats} isLive={isToday} currentGame={currentGame} range={activeWindow} onClose={closeModal} />
    </>
  );
};

const FacilitiesSkeleton = () => (
  <div className="cx-content" aria-busy="true" aria-label="Loading">
    <div className="cx-kpi-grid">
      {[0, 1, 2, 3].map((i) => <div key={i} className="cx-skel" style={{ height: 118, borderRadius: 20 }} />)}
    </div>
    <div className="fx-rcard-grid">
      {[0, 1, 2, 3].map((i) => <div key={i} className="cx-skel" style={{ height: 260, borderRadius: 20 }} />)}
    </div>
  </div>
);

const FacilitiesLocation = () => {
  // Overview (compare games) or Game Day, like the other verticals
  const [dayType, setDayType] = useState('game');
  return (
    <FacilitiesLocationController>
      {({
        loading,
        backgroundLoading,
        currentLocation,
        games,
        now,
        currentSeries,
        currentGame,
        currentSeason,
        isToday,
        selectedDate,
        restrooms,
        lastUpdated,
        autoFetchEnabled,
        setCurrentSeason,
        setCurrentGame,
        setCurrentSeries,
        setSelectedDate,
        authCtx
      }) => (
        <>
          {authCtx.loggedIn ? (
            <div className="cx-page">
              {/* TEMPORARY: remove with dummyLiveData.js */}
              {USE_DUMMY_FACILITIES_DATA && (
                <div style={{ background: '#d97706', color: '#fff', textAlign: 'center', padding: '4px', fontWeight: 600 }}>
                  Dummy data mode: schedule and restroom data are fake (local dev only)
                </div>
              )}
              <div className="sticky-top cx-topbar">
                <FacilitiesHeader
                  currentLocation={currentLocation}
                  isToday={isToday}
                  selectedDate={selectedDate}
                  dayType={dayType}
                  setDayType={setDayType}
                />
                {dayType === 'game' && (
                  <GameSelector
                    loading={loading}
                    currentLocation={currentLocation}
                    currentSeries={currentSeries}
                    currentGame={currentGame}
                    currentSeason={currentSeason}
                    setCurrentSeason={setCurrentSeason}
                    games={games}
                    now={now}
                    isToday={isToday}
                    setCurrentGame={setCurrentGame}
                    setCurrentSeries={setCurrentSeries}
                    setSelectedDate={setSelectedDate}
                  />
                )}

                {/* Thin bar while live data refreshes in the background */}
                {dayType === 'game' && backgroundLoading && autoFetchEnabled && (
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, width: "100%", zIndex: 9999 }}>
                    <LinearProgress
                      sx={{
                        backgroundColor: "rgba(2, 166, 147, 0.25)",
                        height: 8,
                        "& .MuiLinearProgress-bar": { backgroundColor: "rgb(2, 166, 147)" },
                      }}
                    />
                  </div>
                )}
              </div>

              {dayType === 'overview' ? (
                <div className="cx-content">
                  <FacilitiesOverview currentLocation={currentLocation} games={games} />
                </div>
              ) : loading && !backgroundLoading ? (
                <FacilitiesSkeleton />
              ) : (
                <div className="cx-content">
                  <FacilitiesDashboard
                    restrooms={restrooms}
                    currentGame={currentGame}
                    selectedDate={selectedDate}
                    isToday={isToday}
                    now={now}
                    lastUpdated={lastUpdated}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="cx-unauthorized">
              <Link to={"/auth"}>
                Not Authorized. Please Log-In with given credentials or contact support.
              </Link>
            </div>
          )}
        </>
      )}
    </FacilitiesLocationController>
  );
};

export default FacilitiesLocation;
